import { useEffect, useRef } from 'preact/hooks';
import type { BandProfile, Profile } from '../core/types';
import { clamp } from '../core/util';
import type { State, Store } from '../state/store';
import { anchors, canLock, physValue } from '../render/style';
import { classBreaks } from '../render/classes';
import { currentGrid } from '../export/classification';

const cssVar = (name: string, fallback: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

/** Greyscale preview, 2-98 stretched so the band is actually legible. */
export function BandThumb({ profile, band }: { profile: Profile; band: BandProfile }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { width, height } = profile.sampleSize;
  useEffect(() => {
    const c = ref.current;
    const plane = profile.planes[band.index];
    if (!c || !plane) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const img = ctx.createImageData(width, height);
    const lo = band.stats.p2;
    const hi = band.stats.p98 > lo ? band.stats.p98 : lo + 1;
    for (let i = 0; i < plane.length; i++) {
      const v = plane[i] as number;
      const o = i * 4;
      if (!Number.isFinite(v)) { img.data[o + 3] = 0; continue; }
      const g = Math.round(clamp((v - lo) / (hi - lo), 0, 1) * 255);
      img.data[o] = img.data[o + 1] = img.data[o + 2] = g;
      img.data[o + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }, [profile, band]);
  return <canvas class="thumb" ref={ref} width={width} height={height}
    title={band.description ? `DESCRIPTION: ${band.description}` : 'no band description in the file'} />;
}

/** Distribution between p2 and p98, square-rooted so small bins stay visible. */
export function BandHistogram({ band }: { band: BandProfile }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const { hist } = band.stats;
    ctx.clearRect(0, 0, c.width, c.height);
    const mx = Math.max(...hist) || 1;
    const bw = c.width / hist.length;
    ctx.fillStyle = cssVar('--hist', '#3D6070');
    hist.forEach((v, i) => {
      const bh = Math.max(v > 0 ? 1 : 0, Math.round(Math.sqrt(v / mx) * (c.height - 2)));
      ctx.fillRect(i * bw, c.height - bh, Math.max(1, bw - 0.5), bh);
    });
  }, [band]);
  return <canvas class="bhist" ref={ref} width={256} height={21} />;
}

/**
 * Distribution of the values actually being stretched — index values, not stored ones —
 * with the min and max as draggable handles. Without this the stretch controls are
 * numbers with no picture behind them.
 */
export function IndexHistogram(
  { store, state, profile }: { store: Store; state: State; profile: Profile },
) {
  const ref = useRef<HTMLCanvasElement>(null);
  const bins = useRef<number[] | null>(null);
  const drag = useRef<'lo' | 'hi' | null>(null);
  const [rlo, rhi] = anchors(state);

  useEffect(() => {
    if (!canLock(state)) { bins.current = null; return; }
    const grid = currentGrid(profile, state);
    if (!grid) { bins.current = null; return; }
    const span = (rhi - rlo) || 1;
    const b = new Array(64).fill(0);
    for (let i = 0; i < grid.vals.length; i++) {
      const v = grid.vals[i]!;
      if (Number.isFinite(v)) b[clamp(Math.floor((v - rlo) / span * 64), 0, 63)]++;
    }
    bins.current = b;
    draw();
  }, [profile, state.mode, state.indexId, state.singleBand, state.customSrc,
    state.rampLo, state.rampHi, state.lockRamp, state.classes]);

  useEffect(draw, [state.lo, state.hi, state.classify, state.classN, state.classMethod]);

  function draw() {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    const b = bins.current;
    if (!b) return;
    const span = (rhi - rlo) || 1;
    const mx = Math.max(...b) || 1;
    const bw = c.width / b.length;
    ctx.fillStyle = cssVar('--hist', '#3D6070');
    b.forEach((v, i) => {
      const bh = Math.max(v > 0 ? 1 : 0, Math.round(Math.sqrt(v / mx) * (c.height - 10)));
      ctx.fillRect(i * bw, c.height - bh, Math.max(1, bw - 0.5), bh);
    });
    const at = (v: number) => clamp((v - rlo) / span, 0, 1) * c.width;
    ctx.fillStyle = 'rgba(13,20,23,.55)';
    ctx.fillRect(0, 0, at(state.lo), c.height);
    ctx.fillRect(at(state.hi), 0, c.width - at(state.hi), c.height);
    ctx.fillStyle = cssVar('--amber', '#F2A93B');
    ctx.fillRect(at(state.lo) - 1, 0, 2, c.height);
    ctx.fillRect(at(state.hi) - 1, 0, 2, c.height);
    if (state.classify) {
      ctx.fillStyle = 'rgba(255,255,255,.45)';
      classBreaks(state.classN, state.classMethod, bins.current)
        .forEach((k) => ctx.fillRect(clamp(k, 0, 1) * c.width - 0.5, c.height - 8, 1, 8));
    }
  }

  if (!canLock(state)) return null;

  const valueAt = (e: PointerEvent): number => {
    const c = ref.current!;
    const r = c.getBoundingClientRect();
    return rlo + clamp((e.clientX - r.left) / r.width, 0, 1) * ((rhi - rlo) || 1);
  };
  const move = (e: PointerEvent) => {
    if (!drag.current) return;
    const x = valueAt(e);
    if (drag.current === 'lo') store.set({ lo: Math.min(x, state.hi - 1e-6) });
    else store.set({ hi: Math.max(x, state.lo + 1e-6) });
  };
  return (
    <canvas class="ihist" ref={ref} width={290} height={46} data-testid="index-hist"
      title="Distribution of the values you are stretching — drag the handles"
      onPointerDown={(e) => {
        const x = valueAt(e as PointerEvent);
        drag.current = Math.abs(x - state.lo) <= Math.abs(x - state.hi) ? 'lo' : 'hi';
        ref.current?.setPointerCapture((e as PointerEvent).pointerId);
        move(e as PointerEvent);
      }}
      onPointerMove={(e) => move(e as PointerEvent)}
      onPointerUp={() => { drag.current = null; }} />
  );
}

/** Physical-unit statistics, the same units the cursor readout reports. */
export function BandStatsRow({ profile, band }: { profile: Profile; band: BandProfile }) {
  const v = (x: number) => physValue(profile, band.index, x);
  const cells: [string, number][] = [
    ['min', v(band.stats.min)], ['p50', v(band.stats.p50)], ['max', v(band.stats.max)],
    ['p2', v(band.stats.p2)], ['mean', v(band.stats.mean)], ['p98', v(band.stats.p98)],
  ];
  return (
    <div class="bstats">
      {cells.map(([k, val]) => (
        <div key={k}><b>{fmt(val)}</b>{k}</div>
      ))}
    </div>
  );
}
const fmt = (v: number): string => {
  if (!isFinite(v)) return '—';
  if (Math.abs(v) >= 10000 || (Math.abs(v) < 0.001 && v !== 0)) return v.toExponential(2);
  return String(Number(v.toFixed(Math.abs(v) < 10 ? 4 : 1)));
};
